import { ChangeEventHandler, ReactNode, RefObject, useCallback, useMemo, useRef } from 'react';
import { Box, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import escapeRegExp from 'lodash.escaperegexp';
import { isAndroid } from '../../../../shared/utils/is';
import { TrackedMention } from './mentionText.utils';
import { useBackdropStyle } from './useBackdropStyle';

interface HighlightPart {
    text: string;
    isSubscriber?: boolean;
}

const buildHighlightParts = (text: string, mentions: TrackedMention[]): HighlightPart[] => {
    if (mentions.length === 0) return [{ text }];
    const pattern = mentions.map((m) => `@${escapeRegExp(m.displayName)}`).join('|');
    const regex = new RegExp(`(${pattern})`);
    return text.split(regex).map((part) => {
        const mention = mentions.find((m) => part === `@${m.displayName}`);
        return mention !== undefined ? { text: part, isSubscriber: mention.isSubscriber } : { text: part };
    });
};

interface MentionHighlightedInputProps {
    inputRef: RefObject<HTMLInputElement | null>;
    trackedMentions: TrackedMention[];
    placeholder?: string;
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onSelect?: React.ReactEventHandler;
    disabled: boolean;
    endAdornment?: ReactNode;
}

/**
 * A TextField that colors @mentions inline.
 *
 * A <textarea> can only render plain text — individual characters can't be styled.
 * This component works around that with two layers stacked in the same position:
 *
 *   - A backdrop <div> behind the textarea renders all the text with colors applied
 *     (blue for subscribers, gray for non-subscribers, normal for everything else).
 *   - The real <textarea> sits on top handling all user interaction (typing, cursor,
 *     selection), but its text color is transparent so only the caret is visible.
 *
 * The user sees the backdrop's colored text while interacting with the real textarea.
 * The two layers stay aligned because the backdrop is positioned directly over the
 * textarea using its offset/size properties, with font and padding copied from its
 * computed styles. This works regardless of MUI's internal layout (e.g. when
 * endAdornments change the textarea's effective width). Scroll positions are synced
 * via onScroll.
 *
 * On Android phones the backdrop is disabled because Android WebView's native
 * textarea text shaping differs from div rendering at the engine level, causing
 * progressive horizontal drift that CSS property matching cannot eliminate.
 * Mentions still function — they just aren't colored in the input on Android.
 */

export const MentionHighlightedInput = (props: MentionHighlightedInputProps) => {
    const { inputRef, trackedMentions, placeholder = '', value, onChange, onSelect, disabled, endAdornment } = props;
    const backdropRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const backdropStyle = useBackdropStyle(inputRef);

    const handleScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
        if (backdropRef.current) {
            backdropRef.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop;
        }
    }, []);

    // mobile.css's legacy `textarea:focus { border-color: ... !important }` rule beats any stylesheet-based
    // override we write here, no matter how specific — !important vs !important comes down to specificity, and
    // trying to out-specify a global legacy rule is brittle. Setting the border color directly on the element via
    // setProperty(..., 'important') sidesteps the cascade entirely: an inline !important always wins over a
    // stylesheet !important, regardless of selector specificity.
    const handleFocus = useCallback(() => {
        inputRef.current?.style.setProperty('border-color', theme.palette.primary.main, 'important');
    }, [inputRef, theme.palette.primary.main]);

    const handleBlur = useCallback(() => {
        inputRef.current?.style.removeProperty('border-color');
    }, [inputRef]);

    const parts = useMemo(() => buildHighlightParts(value, trackedMentions), [value, trackedMentions]);

    const showBackdrop = !isAndroid() && trackedMentions.length > 0;

    return (
        <Box sx={{ position: 'relative', flex: 1 }}>
            {showBackdrop && (
                <Box
                    ref={backdropRef}
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        ...backdropStyle,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'text.primary'
                    }}
                >
                    {parts.map((part, i) => (
                        <Box
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            component="span"
                            sx={
                                part.isSubscriber !== undefined
                                    ? { color: part.isSubscriber ? 'primary.main' : 'text.disabled' }
                                    : undefined
                            }
                        >
                            {part.text}
                        </Box>
                    ))}
                </Box>
            )}
            <TextField
                fullWidth
                multiline
                maxRows={4}
                variant="filled"
                size="small"
                hiddenLabel
                aria-label={placeholder}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onSelect={onSelect}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={disabled}
                sx={{
                    width: '100%',
                    '& .MuiFilledInput-root': {
                        padding: 0
                    },
                    // slotProps.htmlInput renders straight onto the native <textarea>, which only accepts real
                    // HTML attributes — it can't take an `sx` prop, so all overrides for it have to live here
                    // instead, targeting its class directly. mobile.css has legacy global rules for `textarea`
                    // (background/text color) and `textarea:focus` (border-color) that are both !important, so
                    // these need !important too — and since they're real stylesheet rules (not inline style),
                    // !important actually takes effect here, unlike in an inline `style` object.
                    '& .MuiInputBase-input': {
                        padding: 1,
                        height: 3,
                        minHeight: `${theme.spacing(3)} !important`,
                        ...(showBackdrop && {
                            caretColor: theme.palette.text.primary,
                            color: 'transparent !important',
                            backgroundColor: 'transparent !important'
                        })
                    }
                }}
                slotProps={{
                    input: {
                        disableUnderline: true,
                        endAdornment
                    },
                    htmlInput: {
                        ref: inputRef,
                        onScroll: showBackdrop ? handleScroll : undefined
                    }
                }}
            />
        </Box>
    );
};
